// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import * as sdk from '@canton-network/dapp-sdk';
import {
  CreatedEvent,
  DefaultApi,
  GetUpdatesPageRequest,
  JsContractEntry,
  JsContractEntryOneOf,
  JsGetUpdatesResponse,
  TransactionFormatTransactionShapeEnum,
  Event,
} from '@digitalasset/json-api-ts-client';
import hash from 'object-hash';
import { z } from 'zod';
import { Credential } from '@daml.js/dtc-credential/lib/Dtc/Credential/V3/Credential';
import { ParticipantAgentBatchOperations } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Batch/ParticipantAgentV1';
import { OperatorConfiguration } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Configuration/Operator';
import { ClawbackReceiverDelegation } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Model/Clawback';
import { TransferOffer } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Model/Transfer';
import { AllocationFactory } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/AllocationFactory';
import {
  ClientOnboarderRequest,
  ClientOnboarderService,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/ClientOnboarder';
import {
  ComplianceService,
  ComplianceServiceRequest,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Compliance';
import { EndInvestorOnboardingOffer } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/EndInvestor';
import { RegistrarHoldingOperationsDelegation } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/HoldingOperationsDelegation';
import {
  InstrumentSecurityControlService,
  InstrumentSecurityControlServiceRequest,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/InstrumentSecurityControl';
import { EndInvestorOnboardingRequest } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Onboarding';
import {
  ParticipantOnboardingRequest,
  ParticipantService,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Participant';
import {
  RegistrarBatchOperations,
  RegistrarService,
  RegistrarServiceRequest,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Registrar';
import { ServiceUpgradeConsent } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Upgrade';
import { Holding } from '@daml.js/dtc-registry-holding/lib/Dtc/Registry/Holding/V3/Holding';
import {
  InstrumentConfiguration,
  InstrumentConfigurationFactory,
} from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Configuration/Instrument';
import {
  RejectedTransfer,
  ExecutedTransfer,
} from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Holding/Transfer';
import { AllowList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/AllowList';
import { DenyList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/Deny';
import { TransferRule } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rule/Transfer';
import { Amulet } from '@daml.js/splice-amulet/lib/Splice/Amulet';
import { AmuletRules } from '@daml.js/splice-amulet/lib/Splice/AmuletRules';
import { OpenMiningRound } from '@daml.js/splice-amulet/lib/Splice/Round';
import { AllocationRequest } from '@daml.js/splice-api-token-allocation-request/lib/Splice/Api/Token/AllocationRequestV1';
import { Allocation } from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { Holding as TokenStandardHolding } from '@daml.js/splice-api-token-holding/lib/Splice/Api/Token/HoldingV1';
import {
  Choice,
  ContractId,
  DisclosedContract,
  InterfaceCompanion,
  Party,
  Template,
} from '@daml/types';
import { PackageName } from '../config/package';
import { ACSHelper } from './ACSHelper';
import { createEventFormat } from './EventFormat';
import InterfaceContract from './InterfaceContract';
import TemplateContract from './TemplateContract';
import { WSSupport } from './WSSupport';
import { filterHoldingsByOperatorAndParty } from './holdingsFilter';

export interface DisablementHistoryEntry {
  contractId: string;
  createdAt: string;
  instrumentId: string;
  action: 'DisablementAdded' | 'DisablementRemoved';
  actor: string;
}

/**
 * Schema for the payload returned in executed transaction responses.
 */
const ExecutedPayloadSchema = z.object({
  updateId: z.string(),
  completionOffset: z.number(),
});

/**
 * Schema for a CreatedEvent from prepareExecute response.
 */
const CreatedEventSchema = z.object({
  contractId: z.string(),
  signatories: z.array(z.string()).optional(),
  observers: z.array(z.string()).optional(),
  contractKey: z.unknown().optional(),
  createArgument: z.unknown(),
});

/**
 * Schema for an ExercisedEvent from prepareExecute response.
 */
const ExercisedEventSchema = z.object({
  exerciseResult: z.unknown().optional(),
});

/**
 * Schema for a CreatedEvent response wrapper.
 */
const CreatedEventResponseSchema = z.object({
  CreatedEvent: CreatedEventSchema,
});

/**
 * Schema for an ExercisedEvent response wrapper.
 */
const ExercisedEventResponseSchema = z.object({
  ExercisedEvent: ExercisedEventSchema,
});

/**
 * Schema for an executed transaction acknowledgement response.
 * This is returned when prepareExecute completes successfully with status "executed".
 * The updateId can then be used to fetch the actual transaction tree.
 * Example:
 * {
 *   "status": "executed",
 *   "commandId": "e87a80a5-f1db-43e7-af0d-875e45ac7441",
 *   "payload": {
 *     "updateId": "1220635a...",
 *     "completionOffset": 230
 *   }
 * }
 */
const ExecutedTxResponseSchema = z.object({
  status: z.string(),
  commandId: z.string(),
  payload: ExecutedPayloadSchema,
});

/**
 * Schema for a transaction event (CreatedEvent or ExercisedEvent).
 */
const TxEventSchema = z.union([CreatedEventResponseSchema, ExercisedEventResponseSchema]);

/**
 * Schema for the full prepareExecute response with event data.
 */
const PrepareExecuteEventResultSchema = z.object({
  tx: TxEventSchema,
});

/**
 * Schema for the full prepareExecute response with status data.
 */
const PrepareExecuteStatusResultSchema = z.object({
  tx: ExecutedTxResponseSchema,
});

/**
 * Combined schema for prepareExecute response (either event-based or status-based).
 */
export const PrepareExecuteResultSchema = z.union([
  PrepareExecuteEventResultSchema,
  PrepareExecuteStatusResultSchema,
]);

export type ExecutedPayload = z.infer<typeof ExecutedPayloadSchema>;
export type CreatedEventData = z.infer<typeof CreatedEventSchema>;
export type ExercisedEventData = z.infer<typeof ExercisedEventSchema>;
export type CreatedEventResponse = z.infer<typeof CreatedEventResponseSchema>;
export type ExercisedEventResponse = z.infer<typeof ExercisedEventResponseSchema>;
export type ExecutedTxResponse = z.infer<typeof ExecutedTxResponseSchema>;
export type TxEvent = z.infer<typeof TxEventSchema>;
export type PrepareExecuteEventResult = z.infer<typeof PrepareExecuteEventResultSchema>;
export type PrepareExecuteStatusResult = z.infer<typeof PrepareExecuteStatusResultSchema>;
export type PrepareExecuteResult = z.infer<typeof PrepareExecuteResultSchema>;

/**
 * Request payload for SpliceProvider.request method.
 */
export interface RequestPayload {
  method: string;
  params?: unknown;
}

/**
 * Local interface matching the SpliceProvider request signature.
 * This avoids importing from @canton-network/core-splice-provider which is a transitive dependency.
 */
export interface SpliceProvider {
  request(args: RequestPayload): Promise<unknown>;
}

/**
 * Extended provider type with optional httpProvider for socket serialization handling.
 */

type SubmitterArgs =
  | {
      actAs?: Party[];
      readAs?: Party[];
      submitter?: never;
    }
  | {
      actAs?: never;
      readAs?: never;
      submitter?: Party;
    };

export type LedgerCreateArgs<T extends object> = {
  argument: T;
} & SubmitterArgs;

export type TransferDisablementType =
  | 'NoneOrNotProvided'
  | 'GlobalLock'
  | 'DOChill'
  | 'ComplianceLock'
  | 'SecurityLock';

export type LedgerExerciseArgs<T extends object, C> = {
  cid: ContractId<T>;
  argument: C;
  disclosedContracts?: DisclosedContract[];
} & SubmitterArgs;

export interface HistoryPage<T extends object> {
  data: TemplateContract<T>[];
  nextPageToken?: string;
}

export default class LedgerApiClientV2 {
  private readApiInstance: DefaultApi;

  private defaultSubmitter: string;

  // needs to be marked with # in order to avoid serialization (LedgerApiClientV2 is serialized when used as key)
  #acsHelper: ACSHelper;

  constructor(readApiInstance: DefaultApi, defaultParty: string, wsSupport: WSSupport) {
    this.readApiInstance = readApiInstance;

    this.defaultSubmitter = defaultParty;
    this.#acsHelper = new ACSHelper(this.readApiInstance, wsSupport);
  }

  async getPreferredPackageVersion(packageName: PackageName, operator: string) {
    const packageVersion = await this.readApiInstance
      .getV2InteractiveSubmissionPreferredPackageVersion(packageName, [
        operator,
        this.defaultSubmitter,
      ])
      .catch((error) => {
        console.warn('No package detected:', error);
        return undefined;
      });
    return packageVersion;
  }

  api() {
    return this.readApiInstance;
  }

  async requestRegistryRegistrarService(
    operator: string,
    registrar: string,
    upgrader: string,
  ): Promise<void> {
    return this.create(RegistrarServiceRequest, {
      operator,
      registrar,
      upgrader,
    });
  }

  async createParticipantOnboardingRequest(
    operator: string,
    compliance: string,
    registrar: string,
    clientOnboarder: string,
    participant: string,
    expiration: string,
    upgrader: string,
  ): Promise<void> {
    return this.create(ParticipantOnboardingRequest, {
      operator,
      compliance,
      registrar,
      clientOnboarder,
      participant,
      expiration,
      upgrader,
    });
  }

  async requestRegistryComplianceService(
    operator: string,
    compliance: string,
    upgrader: string,
  ): Promise<void> {
    return this.create(ComplianceServiceRequest, {
      operator,
      compliance,
      upgrader,
    });
  }

  async terminateRegistryComplianceService(
    complianceServiceCid: ContractId<ComplianceService>,
    actor: string,
  ): Promise<void> {
    return this.exercise(ComplianceService.ComplianceService_Terminate, complianceServiceCid, {
      actor,
    });
  }

  async requestRegistryInstrumentSecurityControlService(
    operator: string,
    instrumentSecurityControl: string,
    upgrader: string,
  ): Promise<void> {
    return this.create(InstrumentSecurityControlServiceRequest, {
      operator,
      instrumentSecurityControl,
      upgrader,
    });
  }

  async terminateRegistryInstrumentSecurityControlService(
    instrumentSecurityControlServiceCid: ContractId<InstrumentSecurityControlService>,
  ): Promise<void> {
    return this.exercise(
      InstrumentSecurityControlService.InstrumentSecurityControlService_Terminate,
      instrumentSecurityControlServiceCid,
      {},
    );
  }

  async acceptRegistryInstrumentSecurityControlServiceRequest(
    instrumentSecurityControlServiceRequestCid: ContractId<InstrumentSecurityControlServiceRequest>,
  ): Promise<void> {
    return this.exercise(
      InstrumentSecurityControlServiceRequest.InstrumentSecurityControlServiceRequest_Accept,
      instrumentSecurityControlServiceRequestCid,
      {},
    );
  }

  async rejectRegistryInstrumentSecurityControlServiceRequest(
    instrumentSecurityControlServiceRequestCid: ContractId<InstrumentSecurityControlServiceRequest>,
  ): Promise<void> {
    return this.exercise(
      InstrumentSecurityControlServiceRequest.InstrumentSecurityControlServiceRequest_Reject,
      instrumentSecurityControlServiceRequestCid,
      {},
    );
  }

  async cancelRegistryInstrumentSecurityControlServiceRequest(
    instrumentSecurityControlServiceRequestCid: ContractId<InstrumentSecurityControlServiceRequest>,
  ): Promise<void> {
    return this.exercise(
      InstrumentSecurityControlServiceRequest.InstrumentSecurityControlServiceRequest_Cancel,
      instrumentSecurityControlServiceRequestCid,
      {},
    );
  }

  async requestRegistryClientOnboarderService(
    operator: string,
    registrar: string,
    clientOnboarder: string,
    upgrader: string,
  ): Promise<void> {
    await this.create(ClientOnboarderRequest, {
      operator,
      registrar,
      clientOnboarder,
      upgrader,
    });
  }

  async terminateRegistryClientOnboarderService(
    clientOnboarderServiceCid: ContractId<ClientOnboarderService>,
    actor: string,
  ): Promise<void> {
    return this.exercise(
      ClientOnboarderService.ClientOnboarderService_Terminate,
      clientOnboarderServiceCid,
      {
        actor,
      },
    );
  }

  async acceptRegistryRegistrarServiceRequest(
    registrarServiceRequestCid: ContractId<RegistrarServiceRequest>,
    compliance: Party,
    instrumentSecurityControl: Party,
    participantAgent: Party,
  ): Promise<void> {
    return this.exercise(
      RegistrarServiceRequest.RegistrarServiceRequest_Accept,
      registrarServiceRequestCid,
      {
        compliance,
        instrumentSecurityControl,
        participantAgent,
      },
    );
  }

  async rejectRegistryRegistrarServiceRequest(
    registrarServiceRequestCid: ContractId<RegistrarServiceRequest>,
    reason: string,
  ): Promise<void> {
    return this.exercise(
      RegistrarServiceRequest.RegistrarServiceRequest_Reject,
      registrarServiceRequestCid,
      { reason },
    );
  }

  async cancelRegistryRegistrarServiceRequest(
    registrarServiceRequestCid: ContractId<RegistrarServiceRequest>,
  ): Promise<void> {
    return this.exercise(
      RegistrarServiceRequest.RegistrarServiceRequest_Cancel,
      registrarServiceRequestCid,
      {},
    );
  }

  async archiveRegistryOperatorConfiguration(
    operatorConfigurationCid: ContractId<OperatorConfiguration>,
  ): Promise<void> {
    return this.exercise(OperatorConfiguration.Archive, operatorConfigurationCid, {});
  }

  async createRegistryServiceUpgradeConsent(operator: string, upgrader: string): Promise<void> {
    return this.create(ServiceUpgradeConsent, { operator, upgrader, authorizer: operator });
  }

  async createRegistryOperatorConfiguration(operator: string, upgrader: string): Promise<void> {
    // The operator's ServiceUpgradeConsent cannot be bundled into this submission:
    // Canton's interactive submission service accepts only one command per prepare
    // request. It is created separately via createRegistryServiceUpgradeConsent.
    return this.create(OperatorConfiguration, { operator, upgrader });
  }

  async createRegistryDtccTokenInstrumentConfiguration(
    registryServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationFactoryCid: ContractId<InstrumentConfigurationFactory>,
    tokenName: string,
    tokenSymbol: string,
    cusip: string,
    shortDescription: string,
    issueType: string,
    issueSubtype: string,
    rawMetadata: { [key: string]: string },
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_CreateDtccTokenInstrumentConfiguration,
      registryServiceCid,
      {
        instrumentConfigurationFactoryCid,
        tokenName,
        tokenSymbol,
        cusip,
        uuid: crypto.randomUUID(),
        shortDescription,
        issueType,
        issueSubtype,
        rawMetadata,
        createdAt: new Date().toISOString(),
      },
    );
  }

  async archiveAndCreateRegistryDtccTokenInstrumentConfiguration(
    registryServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationFactoryCid: ContractId<InstrumentConfigurationFactory>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
    tokenName: string,
    tokenSymbol: string,
    cusip: string,
    shortDescription: string,
    issueType: string,
    issueSubtype: string,
    rawMetadata: { [key: string]: string },
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_ArchiveAndCreateDtccTokenInstrumentConfiguration,
      registryServiceCid,
      {
        cid: instrumentConfigurationCid,
        instrumentConfigurationFactoryCid,
        tokenName,
        tokenSymbol,
        cusip,
        shortDescription,
        issueType,
        issueSubtype,
        rawMetadata,
      },
    );
  }

  async addWalletsToAllowList(
    registrarServiceCid: ContractId<RegistrarService>,
    allowListCid: ContractId<AllowList>,
    credentialCids: ContractId<Credential>[],
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_AddWalletsToAllowList,
      registrarServiceCid,
      {
        allowListCid,
        credentialCids,
      },
    );
  }

  async removeWalletsFromAllowList(
    registrarServiceCid: ContractId<RegistrarService>,
    allowListCid: ContractId<AllowList>,
    credentialCids: ContractId<Credential>[],
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_RemoveWalletsFromAllowList,
      registrarServiceCid,
      {
        allowListCid,
        credentialCids,
      },
    );
  }

  async addWalletsToClientOnboarderDenyList(
    clientOnboardServiceCid: ContractId<ClientOnboarderService>,
    denyListCid: ContractId<DenyList>,
    credentialCids: ContractId<Credential>[],
  ): Promise<void> {
    return this.exercise(
      ClientOnboarderService.ClientOnboarderService_DenyWallets,
      clientOnboardServiceCid,
      {
        denyListCid,
        credentialCids,
      },
    );
  }

  async removeWalletsFromClientOnboarderDenyList(
    clientOnboardServiceCid: ContractId<ClientOnboarderService>,
    denyListCid: ContractId<DenyList>,
    credentialCids: ContractId<Credential>[],
  ): Promise<void> {
    return this.exercise(
      ClientOnboarderService.ClientOnboarderService_AllowWallets,
      clientOnboardServiceCid,
      {
        denyListCid,
        credentialCids,
      },
    );
  }

  async participantAddWalletsToAllowList(
    participantServiceCid: ContractId<ParticipantService>,
    allowListCid: ContractId<AllowList>,
    credentialCids: ContractId<Credential>[],
  ): Promise<void> {
    return this.exercise(
      ParticipantService.ParticipantService_AddWalletsToAllowList,
      participantServiceCid,
      {
        allowListCid,
        credentialCids,
      },
    );
  }

  async participantRemoveWalletsFromAllowList(
    participantServiceCid: ContractId<ParticipantService>,
    allowListCid: ContractId<AllowList>,
    credentialCids: ContractId<Credential>[],
  ): Promise<void> {
    return this.exercise(
      ParticipantService.ParticipantService_RemoveWalletsFromAllowList,
      participantServiceCid,
      {
        allowListCid,
        credentialCids,
      },
    );
  }

  async pauseRegistryInstrumentConfiguration(
    instrumentSecurityControlServiceCid: ContractId<InstrumentSecurityControlService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      InstrumentSecurityControlService.InstrumentSecurityControlService_PauseInstrument,
      instrumentSecurityControlServiceCid,
      { instrumentConfigurationCid },
    );
  }

  async unpauseRegistryInstrumentConfiguration(
    instrumentSecurityControlServiceCid: ContractId<InstrumentSecurityControlService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      InstrumentSecurityControlService.InstrumentSecurityControlService_UnpauseInstrument,
      instrumentSecurityControlServiceCid,
      { instrumentConfigurationCid },
    );
  }

  async enableFractionalSupportRegistryInstrumentConfiguration(
    registryServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_EnableFractionalSupport,
      registryServiceCid,
      {
        instrumentConfigurationCid,
      },
    );
  }

  async disableFractionalSupportRegistryInstrumentConfiguration(
    registryServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_DisableFractionalSupport,
      registryServiceCid,
      {
        instrumentConfigurationCid,
      },
    );
  }

  async disableTransfersOnRegistryInstrumentConfiguration(
    registrarServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
    disablementType: TransferDisablementType,
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_AddDisablementOnInstrument,
      registrarServiceCid,
      { cid: instrumentConfigurationCid, disablementType },
    );
  }

  async enableTransfersOnRegistryInstrumentConfiguration(
    registrarServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
    disablementType: TransferDisablementType,
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_RemoveDisablementOnInstrument,
      registrarServiceCid,
      { cid: instrumentConfigurationCid, disablementType },
    );
  }

  async addComplianceLockOnRegistryInstrumentConfiguration(
    complianceServiceCid: ContractId<ComplianceService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      ComplianceService.ComplianceService_AddComplianceLockDisablementOnInstrument,
      complianceServiceCid,
      { instrumentConfigurationCid },
    );
  }

  async removeComplianceLockOnRegistryInstrumentConfiguration(
    complianceServiceCid: ContractId<ComplianceService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      ComplianceService.ComplianceService_RemoveComplianceLockDisablementOnInstrument,
      complianceServiceCid,
      { instrumentConfigurationCid },
    );
  }

  async addSecurityLockOnRegistryInstrumentConfiguration(
    instrumentSecurityControlServiceCid: ContractId<InstrumentSecurityControlService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      InstrumentSecurityControlService.InstrumentSecurityControlService_AddSecurityLockDisablementOnInstrument,
      instrumentSecurityControlServiceCid,
      { instrumentConfigurationCid },
    );
  }

  async removeSecurityLockOnRegistryInstrumentConfiguration(
    instrumentSecurityControlServiceCid: ContractId<InstrumentSecurityControlService>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      InstrumentSecurityControlService.InstrumentSecurityControlService_RemoveSecurityLockDisablementOnInstrument,
      instrumentSecurityControlServiceCid,
      { instrumentConfigurationCid },
    );
  }

  async archiveRegistryInstrumentConfiguration(
    registryServiceCid: ContractId<RegistrarService>,
    instrumentConfigurationFactoryCid: ContractId<InstrumentConfigurationFactory>,
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>,
  ): Promise<void> {
    return this.exercise(
      RegistrarService.RegistrarService_ArchiveInstrumentConfiguration,
      registryServiceCid,
      {
        instrumentConfigurationFactoryCid,
        cid: instrumentConfigurationCid,
      },
    );
  }

  async tapCoin(
    amuletRulesCid: ContractId<AmuletRules>,
    receiver: string,
    amount: string,
    openRound: ContractId<OpenMiningRound>,
  ): Promise<void> {
    return this.exercise(AmuletRules.AmuletRules_DevNet_Tap, amuletRulesCid, {
      receiver,
      amount,
      openRound,
    });
  }

  async queryCredentials(offset: number): Promise<TemplateContract<Credential>[]> {
    return this.query(offset, Credential);
  }

  async queryRegistryOperatorConfiguration(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<OperatorConfiguration> | null> {
    const configs = await this.query(offset, OperatorConfiguration);
    return configs.find((r) => r.payload.operator === operator) || null;
  }

  async queryRegistryOperatorConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<OperatorConfiguration>[]> {
    const configs = await this.query(offset, OperatorConfiguration);
    return configs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryInstrumentConfigurationFactory(
    offset: number,
    // This should really be the registrar, but the operator is fine as long as there's only one registrar.
    operator: string,
  ): Promise<TemplateContract<InstrumentConfigurationFactory>> {
    const results = await this.query(offset, InstrumentConfigurationFactory).then((rs) => {
      return rs.filter((r) => r.payload.operator === operator);
    });
    switch (results.length) {
      case 0:
        return Promise.reject(new Error('No `InstrumentConfigurationFactory` contract found'));
      case 1:
        return results[0];
      default:
        return Promise.reject(
          new Error('Multiple `InstrumentConfigurationFactory` contracts found'),
        );
    }
  }

  async queryRegistryAllocationFactories(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AllocationFactory>[]> {
    const results = await this.query(offset, AllocationFactory);
    return results.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryTransferRules(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<TransferRule>[]> {
    const results = await this.query(offset, TransferRule);
    return results.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryRegistrarServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistrarServiceRequest>[]> {
    const reqs = await this.query(offset, RegistrarServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryRegistrarServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistrarService>[]> {
    const reqs = await this.query(offset, RegistrarService);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryRegistryRegistrarHoldingOperationsDelegations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistrarHoldingOperationsDelegation>[]> {
    const reqs = await this.query(offset, RegistrarHoldingOperationsDelegation);

    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryAllowLists(offset: number, operator: string): Promise<TemplateContract<AllowList>[]> {
    const allowLists = await this.query(offset, AllowList);
    return allowLists.filter((a) => a.payload.operator === operator);
  }

  async queryOperatorDenyLists(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<DenyList>[]> {
    const denyLists = await this.query(offset, DenyList);
    return denyLists.filter((a) => a.payload.operator === operator);
  }

  async queryRegistryRegistrarService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<RegistrarService> | null> {
    const svcs = await this.query(offset, RegistrarService);
    return (
      svcs.find((r) => r.payload.operator === operator && r.payload.registrar === party) || null
    );
  }

  async queryComplianceService(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ComplianceService> | null> {
    const svcs = await this.query(offset, ComplianceService);
    return svcs.find((r) => r.payload.operator === operator) || null;
  }

  async queryInstrumentSecurityControlService(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstrumentSecurityControlService> | null> {
    const svcs = await this.query(offset, InstrumentSecurityControlService);
    return svcs.find((r) => r.payload.operator === operator) || null;
  }

  async queryInstrumentSecurityControlServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstrumentSecurityControlService>[]> {
    const svcs = await this.query(offset, InstrumentSecurityControlService);
    return svcs.filter((r) => r.payload.operator === operator);
  }

  async queryInstrumentSecurityControlServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstrumentSecurityControlServiceRequest>[]> {
    const reqs = await this.query(offset, InstrumentSecurityControlServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryComplianceServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ComplianceService>[]> {
    const svcs = await this.query(offset, ComplianceService);
    return svcs.filter((r) => r.payload.operator === operator);
  }

  async queryComplianceServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ComplianceServiceRequest>[]> {
    const reqs = await this.query(offset, ComplianceServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryClawbackReceiverDelegation(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ClawbackReceiverDelegation>[]> {
    const delegations = await this.query(offset, ClawbackReceiverDelegation);
    return delegations.filter((r) => r.payload.operator === operator);
  }

  async queryServiceUpgradeConsents(
    offset: number,
  ): Promise<TemplateContract<ServiceUpgradeConsent>[]> {
    return this.query(offset, ServiceUpgradeConsent);
  }

  async queryRegistrarBatchOperations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistrarBatchOperations>[]> {
    const batchOps = await this.query(offset, RegistrarBatchOperations);
    return batchOps.filter((r) => r.payload.operator === operator);
  }

  async queryParticipantAgentBatchOperations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ParticipantAgentBatchOperations>[]> {
    const batchOps = await this.query(offset, ParticipantAgentBatchOperations);
    return batchOps.filter((r) => r.payload.operator === operator);
  }

  async queryClientOnboarderService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<ClientOnboarderService> | null> {
    const svcs = await this.query(offset, ClientOnboarderService);
    return (
      svcs.find((r) => r.payload.operator === operator && r.payload.clientOnboarder === party) ||
      null
    );
  }

  async queryClientOnboarderServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ClientOnboarderService>[]> {
    const svcs = await this.query(offset, ClientOnboarderService);
    return svcs.filter((r) => r.payload.operator === operator);
  }

  async queryClientOnboarderServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ClientOnboarderRequest>[]> {
    const reqs = await this.query(offset, ClientOnboarderRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryEndInvestorOnboardingOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<EndInvestorOnboardingOffer>[]> {
    const offers = await this.query(offset, EndInvestorOnboardingOffer);
    return offers.filter((r) => r.payload.operator === operator);
  }

  async queryEndInvestorOnboardingRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<EndInvestorOnboardingRequest>[]> {
    const requests = await this.query(offset, EndInvestorOnboardingRequest);
    return requests.filter((r) => r.payload.operator === operator);
  }

  async queryParticipantOnboardingRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ParticipantOnboardingRequest>[]> {
    const requests = await this.query(offset, ParticipantOnboardingRequest);
    return requests.filter((r) => r.payload.operator === operator);
  }

  async queryParticipantServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ParticipantService>[]> {
    const services = await this.query(offset, ParticipantService);
    return services.filter((r) => r.payload.operator === operator);
  }

  async queryHoldings(offset: number, operator: string): Promise<TemplateContract<Holding>[]> {
    const res = await this.query(offset, Holding);
    return filterHoldingsByOperatorAndParty(res, operator, this.defaultSubmitter);
  }

  async queryInstrumentConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstrumentConfiguration>[]> {
    const res = await this.query(offset, InstrumentConfiguration);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryTransferOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<TransferOffer>[]> {
    const res = await this.query(offset, TransferOffer);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryRejectedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedTransfer>[]> {
    const res = await this.query(offset, RejectedTransfer);
    return res.filter((r) => r.payload.transfer.operator === operator);
  }

  async queryExecutedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedTransfer>[]> {
    const res = await this.query(offset, ExecutedTransfer);
    return res.filter((r) => r.payload.transfer.operator === operator);
  }

  async queryCoins(offset: number, party: string): Promise<TemplateContract<Amulet>[]> {
    const res = await this.query(offset, Amulet);
    return res.filter((r) => r.payload.owner === party);
  }

  async queryTokenStandardHoldings(
    offset: number,
  ): Promise<InterfaceContract<TokenStandardHolding>[]> {
    return this.queryInterface(offset, TokenStandardHolding);
  }

  async queryAllocations(offset: number): Promise<InterfaceContract<Allocation>[]> {
    return this.queryInterface(offset, Allocation);
  }

  async queryAllocationRequestInterface(
    offset: number,
  ): Promise<InterfaceContract<AllocationRequest>[]> {
    return this.queryInterface(offset, AllocationRequest);
  }

  async create<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    argument: T,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<void> {
    const commandId = hash({
      templateId: template.templateId,
      argument,
      actAs,
      readAs,
    });

    const command = {
      CreateCommand: {
        templateId: template.templateId,
        createArguments: template.encode(argument),
      },
    };

    console.log('before create', command);

    await sdk.prepareExecuteAndWait({
      commandId,
      commands: [command],
      actAs,
      readAs,
      synchronizerId: undefined,
    });
  }

  async exercise<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    disclosedContracts?: DisclosedContract[],
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<void> {
    const { templateId } = choice.template();
    const { choiceName } = choice;
    const contractId = cid;

    const commandId = hash({
      contractId: cid,
      templateId,
      choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId,
        contractId,
        choice: choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    console.log('before exercise', command);

    await sdk.prepareExecuteAndWait({
      commandId,
      commands: [command],
      actAs,
      readAs,
      disclosedContracts,
      synchronizerId: undefined,
    });
  }

  async exerciseWithResult<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    disclosedContracts?: DisclosedContract[],
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<R> {
    const { templateId } = choice.template();
    const { choiceName } = choice;
    const contractId = cid;

    const commandId = hash({
      contractId: cid,
      templateId,
      choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId,
        contractId,
        choice: choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    console.log('before exercise', command);

    const response = await sdk.prepareExecuteAndWait({
      commandId,
      commands: [command],
      actAs,
      readAs,
      disclosedContracts,
      synchronizerId: undefined,
    });

    const { updateId } = response.tx.payload;
    const treeResponse = await this.readApiInstance.getV2UpdatesTransactionTreeByIdUpdateId(
      updateId,
      actAs,
    );
    const tree = treeResponse.transaction;

    for (const event of Object.values(tree.eventsById)) {
      if ('ExercisedTreeEvent' in event) {
        const exercisedEvent = event.ExercisedTreeEvent.value;
        if (exercisedEvent.choice === choiceName && exercisedEvent.contractId === (cid as string)) {
          return choice.resultDecoder.runWithException(exercisedEvent.exerciseResult);
        }
      }
    }

    throw new Error(`No exercise result found for choice ${choiceName}`);
  }

  private async query<T extends object, K, I extends string>(
    offset: number,
    template: Template<T, K, I>,
    party: string = this.defaultSubmitter,
  ): Promise<TemplateContract<T>[]> {
    return this.activeContractsForTemplate(offset, party, template.templateId).then((responses) => {
      return responses
        .map((resp) => resp.contractEntry)
        .filter(LedgerApiClientV2.isJsActiveContract)
        .map((activeContract) =>
          TemplateContract.fromJsonAPIV2(template, activeContract.JsActiveContract.createdEvent),
        );
    });
  }

  /**
   * Read a page of the creation history of a template.
   *
   * Unlike `query`, which reads the active contract set, this reads the
   * transaction stream (ledger-effects shape) and returns the `CreatedEvent`s
   * of `template`. This is the only way to observe "marker" contracts that are
   * created and archived within the same transaction: such transient contracts
   * produce no ACS delta, so they never appear in the ACS or the ACS-delta
   * stream, but they are present in the ledger-effects projection.
   *
   * Returns a single page plus a `nextPageToken` cursor; callers paginate by
   * passing the token back in. To keep paging consistent across calls, pin
   * `endOffset` (e.g. to the ledger end captured once) and reuse it for every
   * page, as required by the JSON Ledger API.
   */
  async queryHistory<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    options: {
      endOffset: number;
      pageSize?: number;
      pageToken?: string;
      party?: string;
    },
  ): Promise<HistoryPage<T>> {
    const party = options.party ?? this.defaultSubmitter;
    const eventFormat = createEventFormat(template, [party], false);
    const request: GetUpdatesPageRequest = {
      endOffsetInclusive: options.endOffset,
      maxPageSize: options.pageSize,
      descendingOrder: true,
      pageToken: options.pageToken,
      updateFormat: {
        includeTransactions: {
          eventFormat,
          transactionShape: TransactionFormatTransactionShapeEnum.TRANSACTION_SHAPE_LEDGER_EFFECTS,
        },
      },
    };
    const response = await this.readApiInstance.postV2UpdatesGetUpdatesPage(request);
    const contracts = LedgerApiClientV2.collectCreatedEvents(response.updates ?? []).map(
      (createdEvent) => TemplateContract.fromJsonAPIV2(template, createdEvent),
    );
    return { data: contracts, nextPageToken: response.nextPageToken };
  }

  private static collectCreatedEvents = (updates: JsGetUpdatesResponse[]): CreatedEvent[] => {
    return updates.flatMap(LedgerApiClientV2.updateEvents).flatMap((event) => {
      const createdEvent = LedgerApiClientV2.asCreatedEvent(event);
      return createdEvent ? [createdEvent] : [];
    });
  };

  private static updateEvents = (updateResponse: JsGetUpdatesResponse): Event[] => {
    return updateResponse.update && 'Transaction' in updateResponse.update
      ? (updateResponse.update.Transaction.value.events ?? [])
      : [];
  };

  private static asCreatedEvent = (event: Event): CreatedEvent | undefined => {
    return 'CreatedEvent' in event ? event.CreatedEvent : undefined;
  };

  private async queryInterface<T extends object, K, I extends string>(
    offset: number,
    interfaceCompanion: InterfaceCompanion<T, K, I>,
    party: string | undefined = this.defaultSubmitter,
  ): Promise<InterfaceContract<T>[]> {
    return this.activeContractsForInterface(offset, party, interfaceCompanion.templateId).then(
      (responses) => {
        return responses
          .map((resp) => resp.contractEntry)
          .filter(LedgerApiClientV2.isJsActiveContract)
          .map((activeContract) =>
            InterfaceContract.fromJsonAPIV2(
              interfaceCompanion,
              activeContract.JsActiveContract.createdEvent,
            ),
          );
      },
    );
  }

  private static isJsActiveContract = (
    contract?: JsContractEntry,
  ): contract is JsContractEntryOneOf => {
    return !!contract && 'JsActiveContract' in contract;
  };

  async activeContractsForTemplate(offset: number, partyFilter: string, templateId: string) {
    return this.#acsHelper.activeContractsForTemplate(offset, partyFilter, templateId);
  }

  async activeContractsForInterface(
    offset: number,
    partyFilter: string | undefined,
    interfaceId: string,
  ) {
    return this.#acsHelper.activeContractsForInterface(offset, partyFilter, interfaceId);
  }
}
